import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContentCard from "@/components/feature/ContentCard";
import { useSeo } from "@/hooks/useSeo";
import { categoryMetas, getItemById, getRelated, typeLabels } from "@/mocks/content";
import MusicDemoPlayer from "./components/MusicDemoPlayer";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getItemById(id) : undefined;
  const [toast, setToast] = useState("");

  useSeo({
    title: item ? `《${item.title}》详情 - 快猫` : "内容不存在 - 快猫",
    description: item
      ? item.summary
      : "该内容已下架或不存在，返回快猫首页浏览更多娱乐内容。",
    keywords: item
      ? `快猫,${item.title},${typeLabels[item.type]},${item.tags.join(",")}`
      : "快猫,内容不存在",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
          <i className="ri-file-unknow-line text-5xl text-foreground-300" />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950">
            该内容已下架或不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你可以返回首页或分类页，继续浏览其他精彩内容。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-full bg-primary-500 px-6 py-2.5 font-label text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/category"
              className="rounded-full border border-background-300/70 px-6 py-2.5 font-label text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              浏览分类
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const meta = categoryMetas.find((m) => m.type === item.type)!;
  const related = getRelated(item, 3);
  const isNews = item.type === "news";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const shareTo = (platform: string) => {
    setToast(`已生成分享到 ${platform} 的内容（演示效果）`);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "分类", to: "/category" }, { label: meta.subtitle, to: meta.path }, { label: item.title }]} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <article>
            <header>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Link
                  to={meta.path}
                  className="rounded-full bg-primary-100 px-2.5 py-1 font-label font-medium text-primary-700 hover:bg-primary-200"
                >
                  {typeLabels[item.type]}
                </Link>
                {item.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-secondary-800">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                {item.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" />
                  {item.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {item.date}
                </span>
                {!isNews && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-fire-line" />
                    热度 {item.heat.toLocaleString()}
                  </span>
                )}
                {item.type === "film" && (
                  <span className="flex items-center gap-1.5 font-medium text-primary-600">
                    <i className="ri-star-fill" />
                    评分 {item.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </header>

            <div className={`mt-6 w-full overflow-hidden rounded-lg ${isNews ? "aspect-[16/9]" : "aspect-[3/2]"}`}>
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 快猫${typeLabels[item.type]}`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            {item.type === "film" && (
              <div className="mt-6 flex flex-wrap gap-3 rounded-lg border border-background-200/80 bg-background-50 p-4 text-sm">
                <span className="text-foreground-600">类型：<strong className="text-foreground-900">{item.genre}</strong></span>
                <span className="text-foreground-600">年代：<strong className="text-foreground-900">{item.year}</strong></span>
                <span className="text-foreground-600">地区：<strong className="text-foreground-900">{item.region}</strong></span>
              </div>
            )}

            {item.type === "music" && (
              <div className="mt-6">
                <MusicDemoPlayer title={item.title} artist={item.author} />
              </div>
            )}

            <section className="mt-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500" />
                {isNews ? "资讯正文" : item.type === "music" ? "作品介绍" : "剧情简介"}
              </h2>
              <p className="mt-4 whitespace-pre-line text-[15px] leading-8 text-foreground-800">
                {item.content}
              </p>
            </section>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200/80 pt-6">
              <button
                type="button"
                onClick={copyLink}
                className="flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-link" />
                复制链接
              </button>
              <button
                type="button"
                onClick={() => shareTo("微博")}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/70 px-5 py-2.5 font-label text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-weibo-line" />
                微博
              </button>
              <button
                type="button"
                onClick={() => shareTo("微信")}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/70 px-5 py-2.5 font-label text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-wechat-line" />
                微信
              </button>
              <Link
                to={meta.path}
                className="ml-auto flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line" />
                返回{meta.subtitle}
              </Link>
            </div>
          </article>

          <aside>
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 lg:sticky lg:top-20">
              <h2 className="font-heading text-base font-extrabold text-foreground-950">相关推荐</h2>
              <p className="mt-1 text-xs text-foreground-500">根据分类与标签为你关联</p>
              <ul className="mt-4 space-y-4">
                {related.map((rel) => (
                  <li key={rel.id}>
                    <Link to={`/detail/${rel.id}`} className="group flex gap-3">
                      <div className="h-16 w-12 shrink-0 overflow-hidden rounded-md">
                        <img
                          src={rel.cover}
                          alt={rel.title}
                          title={`${rel.title} - 快猫`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {rel.title}
                        </p>
                        <p className="mt-1 text-xs text-foreground-500">{rel.author}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <h2 className="mb-4 font-heading text-xl font-extrabold text-foreground-950">更多{typeLabels[item.type]}推荐</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((rel) => (
              <ContentCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-3 font-label text-sm text-background-50">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-primary-400" />
            {toast}
          </span>
        </div>
      )}
    </SiteLayout>
  );
}